// src/admin/AdminProducts.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PlusIcon, SearchIcon, EditIcon, TrashIcon, LayersIcon,
  CheckCircleIcon, AlertTriangleIcon, XCircleIcon,
  GridIcon, ListIcon, ImageIcon, PackageIcon, RefreshIcon,
} from './ProductIcons';

function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStock, setFilterStock] = useState('all');
  const [view, setView] = useState('table'); // 'table' | 'grid'

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get('https://madadline.com/api/products').catch(() => ({ data: [] })),
        axios.get('https://madadline.com/api/categories').catch(() => ({ data: [] })),
      ]);

      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);

      const list = Array.isArray(catRes.data)
        ? catRes.data
          .map(item => {
            if (typeof item === 'string') return { id: item, name: item };
            return { id: item.id ?? item.name, name: item.name ?? '' };
          })
          .filter(c => c.name)
        : [];
      setCategories(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  /* =========================================================
     SUPPRESSION
  ========================================================= */
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`https://madadline.com/api/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      alert('Erreur lors de la suppression.');
    }
  };

  /* =========================================================
     ⭐ CALCUL DU STOCK TOTAL (basé sur les variantes)
  ========================================================= */
  const getTotalStock = (product) => {
    // Si le produit a des variantes, on additionne leurs stocks
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce(
        (total, v) => total + (Number(v.stock) || 0),
        0
      );
    }
    // Sinon, on renvoie le stock global du produit
    return Number(product.stock) || 0;
  };

  /* =========================================================
     FILTRES
  ========================================================= */
  const filtered = useMemo(() => {
    return products.filter(p => {
      if (filterCat && !getCategories(p).includes(filterCat)) return false;
      if (search && !(p.nom || '').toLowerCase().includes(search.toLowerCase())) return false;

      const totalStock = getTotalStock(p);

      if (filterStock === 'in') return totalStock > 0;
      if (filterStock === 'low') return totalStock > 0 && totalStock <= 3;
      if (filterStock === 'out') return totalStock === 0;

      return true;
    });
  }, [products, filterCat, search, filterStock]);

  /* =========================================================
     STATS (basées sur le stock total)
  ========================================================= */
  const stats = useMemo(() => {
    let inStock = 0, lowStock = 0, outStock = 0;

    products.forEach(p => {
      const total = getTotalStock(p);
      if (total > 3) inStock++;
      else if (total > 0) lowStock++;
      else outStock++;
    });

    return {
      total: products.length,
      inStock,
      lowStock,
      outStock,
    };
  }, [products]);


  /* =========================================================
     HELPERS — IMAGES ET CATÉGORIES MULTIPLES
  ========================================================= */

  const getImg = (product) => {
    const raw = product?.image || product?.image_url || '';

    if (!raw) return '/placeholder.png';

    // Image distante
    if (/^https?:\/\//i.test(raw)) return raw;

    // Nettoyer le chemin enregistré dans MySQL
    const path = raw.replace(/^\/+/, '');
    const filename = path.split('/').pop();

    // Les images sont servies par le backend Laravel
    if (path.startsWith('images/')) {
      return `https://madadline.com/api/images/${encodeURIComponent(filename)}`;
    }

    return `https://madadline.com/${path}`;
  };

  const getCategories = (product) => {
    // Nouveau système : relation plusieurs-à-plusieurs
    if (Array.isArray(product?.categories) && product.categories.length > 0) {
      return product.categories
        .map(category => category.name)
        .filter(Boolean);
    }

    // Compatibilité avec les anciens produits
    return product?.categorie ? [product.categorie] : [];
  };

  const getStockBadge = (stock) => {
    if (stock > 3) return { cls: 'ok', label: stock, Icon: CheckCircleIcon };
    if (stock > 0) return { cls: 'low', label: stock, Icon: AlertTriangleIcon };
    return { cls: 'out', label: 'Épuisé', Icon: XCircleIcon };
  };


  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="ap">

      {/* ===== HEADER ===== */}
      <header className="ap-head">
        <div className="ap-head__left">
          <h1 className="ap-head__title">Produits</h1>
          <p className="ap-head__sub">
            {stats.total} produit{stats.total > 1 ? 's' : ''} en catalogue
          </p>
        </div>
        <div className="ap-head__actions">
          <button className="ap-iconbtn" onClick={fetchData} title="Rafraîchir">
            <RefreshIcon size={18} />
          </button>
          <Link to="/admin/produits/nouveau" className="ap-btn ap-btn--primary">
            <PlusIcon size={16} />
            <span>Nouveau produit</span>
          </Link>
        </div>
      </header>

      {/* ===== KPIs ===== */}
      <section className="ap-kpis">
        <div className="ap-kpi ap-kpi--success">
          <div className="ap-kpi__icon"><CheckCircleIcon size={22} /></div>
          <div className="ap-kpi__body">
            <div className="ap-kpi__value">{stats.inStock}</div>
            <div className="ap-kpi__label">En stock</div>
          </div>
        </div>
        <div className="ap-kpi ap-kpi--warning">
          <div className="ap-kpi__icon"><AlertTriangleIcon size={22} /></div>
          <div className="ap-kpi__body">
            <div className="ap-kpi__value">{stats.lowStock}</div>
            <div className="ap-kpi__label">Stock faible</div>
          </div>
        </div>
        <div className="ap-kpi ap-kpi--danger">
          <div className="ap-kpi__icon"><XCircleIcon size={22} /></div>
          <div className="ap-kpi__body">
            <div className="ap-kpi__value">{stats.outStock}</div>
            <div className="ap-kpi__label">Épuisé</div>
          </div>
        </div>
        <div className="ap-kpi ap-kpi--neutral">
          <div className="ap-kpi__icon"><PackageIcon size={22} /></div>
          <div className="ap-kpi__body">
            <div className="ap-kpi__value">{stats.total}</div>
            <div className="ap-kpi__label">Total</div>
          </div>
        </div>
      </section>

      {/* ===== FILTRES ===== */}
      <div className="ap-filters">
        <div className="ap-search">
          <SearchIcon size={16} className="ap-search__icon" />
          <input
            type="text"
            placeholder="Rechercher un produit..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ap-search__input"
          />
        </div>

        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          className="ap-select"
        >
          <option value="">Toutes les catégories</option>
          {categories.map(c => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>

        <select
          value={filterStock}
          onChange={e => setFilterStock(e.target.value)}
          className="ap-select"
        >
          <option value="all">Tous les stocks</option>
          <option value="in">En stock</option>
          <option value="low">Stock faible (≤3)</option>
          <option value="out">Épuisé</option>
        </select>

        <div className="ap-viewtoggle">
          <button
            className={`ap-viewtoggle__btn ${view === 'table' ? 'is-active' : ''}`}
            onClick={() => setView('table')}
            title="Vue tableau"
          >
            <ListIcon size={16} />
          </button>
          <button
            className={`ap-viewtoggle__btn ${view === 'grid' ? 'is-active' : ''}`}
            onClick={() => setView('grid')}
            title="Vue grille"
          >
            <GridIcon size={16} />
          </button>
        </div>
      </div>

      {/* ===== CONTENU ===== */}
      {loading ? (
        <div className="ap-loader"><div className="ap-spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="ap-empty">
          <PackageIcon size={40} style={{ color: '#C21E56', opacity: 0.4 }} />
          <p>Aucun produit trouvé.</p>
        </div>
      ) : view === 'table' ? (
        /* ===== VUE TABLEAU ===== */
        <div className="ap-card">
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th>Prix</th>
                  <th>Stock (Variantes)</th>
                  <th className="ap-table__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const totalStock = getTotalStock(p);
                  const badge = getStockBadge(totalStock);
                  const BadgeIcon = badge.Icon;

                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="ap-prodcell">
                          <img src={getImg(p)} alt={p.nom} className="ap-prodcell__img" />
                          <div className="ap-prodcell__info">
                            <div className="ap-prodcell__name">{p.nom}</div>
                            {p.nom_en && (
                              <div className="ap-prodcell__name-en">{p.nom_en}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '5px'
                        }}>
                          {getCategories(p).length > 0 ? (
                            getCategories(p).map((name, index) => (
                              <span className="ap-cat" key={`${name}-${index}`}>
                                {name}
                              </span>
                            ))
                          ) : (
                            <span>—</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <strong className="ap-price">{p.prix} <span className="ap-price__cur">DH</span></strong>
                      </td>
                      <td>
                        <span className={`ap-stock ap-stock--${badge.cls}`}>
                          <BadgeIcon size={12} />
                          {badge.label}
                        </span>
                      </td>
                      <td>
                        <div className="ap-actions">
                          <Link
                            to={`/admin/produits/${p.id}`}
                            className="ap-iconbtn ap-iconbtn--edit"
                            title="Modifier"
                          >
                            <EditIcon size={15} />
                          </Link>
                          <button
                            onClick={() => navigate(`/admin/produits/${p.id}/variantes`)}
                            className="ap-iconbtn ap-iconbtn--variant"
                            title="Variantes"
                          >
                            <LayersIcon size={15} />
                          </button>
                          <button
                            onClick={() => handleDelete(p.id, p.nom)}
                            className="ap-iconbtn ap-iconbtn--danger"
                            title="Supprimer"
                          >
                            <TrashIcon size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ===== VUE GRILLE ===== */
        <div className="ap-grid">
          {filtered.map(p => {
            const totalStock = getTotalStock(p);
            const badge = getStockBadge(totalStock);

            return (
              <div key={p.id} className="ap-cardp">
                <div className="ap-cardp__imgwrap">
                  <img src={getImg(p)} alt={p.nom} className="ap-cardp__img" />
                  <span className={`ap-stock ap-stock--${badge.cls} ap-cardp__badge`}>
                    {badge.label}
                  </span>
                </div>
                <div className="ap-cardp__body">
                  <div className="ap-cardp__name">{p.nom}</div>
                  <div className="ap-cardp__cat">{p.categorie || '—'}</div>
                  <div className="ap-cardp__price">
                    {p.prix} <span className="ap-price__cur">DH</span>
                  </div>
                  <div className="ap-cardp__actions">
                    <Link
                      to={`/admin/produits/${p.id}`}
                      className="ap-iconbtn ap-iconbtn--edit"
                      title="Modifier"
                    >
                      <EditIcon size={14} />
                    </Link>
                    <button
                      onClick={() => navigate(`/admin/produits/${p.id}/variantes`)}
                      className="ap-iconbtn ap-iconbtn--variant"
                      title="Variantes"
                    >
                      <LayersIcon size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id, p.nom)}
                      className="ap-iconbtn ap-iconbtn--danger"
                      title="Supprimer"
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default AdminProducts;